import { DOMHelpers } from 'helpers/dom';
import { Func } from 'interfaces/func';
import { Observable, SubscribableOrNullableValue } from 'knockout';
import { withEffects } from 'mixins/withEffects';

export interface DragAndDropManagerConfig<T> {
    element: SubscribableOrNullableValue<HTMLElement>
    extractor: Func<T, [DataTransfer]> | Func<Promise<T>, [DataTransfer]>
    disabled?: SubscribableOrNullableValue<boolean>

    onDrop: Action<[T, JQuery.TriggeredEvent<HTMLElement, undefined, HTMLElement, HTMLElement>]>    
}

export default class DragAndDropManager<T> {
    private effects = withEffects();

    private element: Observable<HTMLElement | undefined>
    private onDropFunc: Action<[T, JQuery.TriggeredEvent<HTMLElement, undefined, HTMLElement, HTMLElement>]>
    private extractor: Func<T, [DataTransfer]> | Func<Promise<T>, [DataTransfer]>

    private dragEntersCount = ko.observable(0);

    active = this.dragEntersCount.is(v => v > 0);

    constructor(config: DragAndDropManagerConfig<T>) {
        this.element = ko.observable(config.element).unwrap();
        this.onDropFunc = config.onDrop || _.noop;
        this.extractor = config.extractor || _.noop;

        const isDisabled = ko.flattenComputed(config.disabled, false);

        this.effects.register((element, isDisabled) => {
            if (element != undefined && !isDisabled) {
                return [
                    DOMHelpers.onNodeEvent(document.body, 'dragenter', this.onDragEnter.bind(this)),
                    DOMHelpers.onNodeEvent(document.body, 'dragover', this.onDragOver.bind(this)),
                    DOMHelpers.onNodeEvent(document.body, 'dragleave', this.onDragLeave.bind(this)),
                    DOMHelpers.onNodeEvent(document.body, 'drop', this.onDropMain.bind(this)),
                    DOMHelpers.onNodeEvent(element, 'drop', this.onDrop.bind(this))
                ];
            }
        }, [this.element, isDisabled]);
    }

    dispose() {
        this.effects.dispose();
    }

    private onDragEnter(e: JQuery.TriggeredEvent<HTMLElement, undefined, HTMLElement, HTMLElement>) {
        e.preventDefault();
        this.dragEntersCount.inc();
    }

    private onDragOver(e: JQuery.TriggeredEvent<HTMLElement, undefined, HTMLElement, HTMLElement>) {
        e.preventDefault();
    }

    private onDragLeave(e: JQuery.TriggeredEvent<HTMLElement, undefined, HTMLElement, HTMLElement>) {
        e.preventDefault();
        this.dragEntersCount.dec();
    }

    private onDropMain(e: JQuery.TriggeredEvent<HTMLElement, undefined, HTMLElement, HTMLElement>) {
        e.preventDefault();
        this.dragEntersCount(0);
    }

    private async onDrop(e: JQuery.TriggeredEvent<HTMLElement, undefined, HTMLElement, HTMLElement>) {
        e.preventDefault();

        const dt = (e.originalEvent as DragEvent).dataTransfer;
        if (dt != undefined) {
            const data = await Promise.resolve(this.extractor(dt));
            this.onDropFunc(data, e);
        }
    }
}